/**
 * Settings screen - sound, language, stats, reset
 */

import { t } from '../i18n/index.js';
import { SUPPORTED_LOCALES, getCurrentLocale, setLocale } from '../i18n/index.js';
import type { Locale } from '../i18n/index.js';
import { getBgmVolume, getSfxVolume, setBgmVolume, setSfxVolume, playSfx } from '../sound.js';
import { formatNumber, formatTime } from '../utils/format.js';
import { dispatch, getState, getViewState, setViewState, onRender, showToast, setInnerHTML } from './renderer.js';
import { getConqueredCount } from '../game/systems/conquest.js';
import { COUNTRIES } from '../game/data/conquest.js';
import { resetConquestCache } from './dominationScreen.js';

const LANG_NAMES: Record<string, string> = {
  en: 'English', ja: '\u65E5\u672C\u8A9E', ko: '\uD55C\uAD6D\uC5B4',
  de: 'Deutsch', es: 'Espa\u00F1ol', pt: 'Portugu\u00EAs',
  fr: 'Fran\u00E7ais', 'zh-TW': '\u7E41\u9AD4\u4E2D\u6587', th: '\u0E44\u0E17\u0E22',
};

export function initSettingsScreen() {
  onRender(renderSettingsScreen);
}

function renderSettingsScreen() {
  if (getViewState().currentTab !== 'settings') return;

  const container = document.getElementById('screen-content');
  if (!container) return;

  const state = getState();
  const currentLang = getCurrentLocale();

  // Language buttons
  const langHtml = SUPPORTED_LOCALES.map(loc =>
    `<button class="lang-btn ${currentLang === loc ? 'active' : ''}" data-lang="${loc}">${LANG_NAMES[loc] || loc}</button>`
  ).join('');

  // Stats
  const statsHtml = `
    <div class="stats-grid">
      <div class="stat-item">
        <div class="stat-label">${t('settings_total_ip')}</div>
        <div class="stat-value">${formatNumber(state.totalIpEarned)}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${t('settings_total_taps')}</div>
        <div class="stat-value">${formatNumber(state.tapCount)}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${t('settings_total_prestiges')}</div>
        <div class="stat-value">${state.prestigeCount}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${t('settings_play_time')}</div>
        <div class="stat-value">${formatTime(state.totalPlayTimeMs)}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${t('settings_conquest')}</div>
        <div class="stat-value">${getConqueredCount(state)}/${COUNTRIES.length}</div>
      </div>
    </div>
  `;

  const settingsHtml = `
    <div class="settings-screen">
      <div class="settings-section">
        <div class="settings-label">\u{1F50A} ${t('settings_bgm')} / ${t('settings_sfx')}</div>
        <div class="settings-row">
          <span class="settings-row-label">${t('settings_bgm')}</span>
          <input type="range" class="settings-slider" id="bgm-slider" min="0" max="100" value="${getBgmVolume()}" />
          <span class="settings-vol-display" id="bgm-vol-display">${getBgmVolume()}</span>
        </div>
        <div class="settings-row">
          <span class="settings-row-label">${t('settings_sfx')}</span>
          <input type="range" class="settings-slider" id="sfx-slider" min="0" max="100" value="${getSfxVolume()}" />
          <span class="settings-vol-display" id="sfx-vol-display">${getSfxVolume()}</span>
        </div>
        <div class="settings-sound-actions">
          <button class="sound-action-btn" id="sfx-test-btn">\u{1F50A} ${t('settings_sfx_test')}</button>
          <button class="sound-action-btn" id="sound-reset-btn">\u{1F504} ${t('settings_sound_reset')}</button>
        </div>
      </div>

      <div class="settings-section">
        <div class="settings-label">\u{1F310} ${t('settings_lang')}</div>
        <div class="lang-selector">${langHtml}</div>
      </div>

      <div class="settings-section">
        <div class="settings-label">\u{1F4CA} ${t('settings_stats')}</div>
        ${statsHtml}
      </div>

      <button class="reset-btn" id="reset-btn">${t('settings_reset')}</button>
    </div>
  `;

  if (!setInnerHTML(container, settingsHtml)) return;

  // Event handlers
  const bgmSlider = document.getElementById('bgm-slider') as HTMLInputElement;
  const bgmDisplay = document.getElementById('bgm-vol-display');
  if (bgmSlider) {
    bgmSlider.addEventListener('input', () => {
      const val = parseInt(bgmSlider.value);
      setBgmVolume(val);
      if (bgmDisplay) bgmDisplay.textContent = String(val);
    });
  }

  const sfxSlider = document.getElementById('sfx-slider') as HTMLInputElement;
  const sfxDisplay = document.getElementById('sfx-vol-display');
  if (sfxSlider) {
    sfxSlider.addEventListener('input', () => {
      const val = parseInt(sfxSlider.value);
      setSfxVolume(val);
      if (sfxDisplay) sfxDisplay.textContent = String(val);
    });
  }

  // SFX test button
  const sfxTestBtn = document.getElementById('sfx-test-btn');
  if (sfxTestBtn) {
    sfxTestBtn.addEventListener('click', () => {
      playSfx('achievement');
    });
  }

  // Sound reset button
  const soundResetBtn = document.getElementById('sound-reset-btn');
  if (soundResetBtn) {
    soundResetBtn.addEventListener('click', () => {
      setBgmVolume(50);
      setSfxVolume(70);
      if (bgmSlider) bgmSlider.value = '50';
      if (sfxSlider) sfxSlider.value = '70';
      if (bgmDisplay) bgmDisplay.textContent = '50';
      if (sfxDisplay) sfxDisplay.textContent = '70';
      playSfx('boost');
      showToast(t('settings_sound_reset_done'));
    });
  }

  container.querySelectorAll('[data-lang]').forEach(btn => {
    btn.addEventListener('click', () => {
      const lang = (btn as HTMLElement).dataset.lang! as Locale;
      setLocale(lang);
      location.reload();
    });
  });

  // Reset button
  const resetBtn = document.getElementById('reset-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (confirm(t('settings_reset_confirm'))) {
        dispatch({ type: 'RESET' });
        setViewState({ conquestSelectedCountry: null });
        resetConquestCache();
        // Restart game loops to reset all closure-captured tracking variables
        // (prevConqueredSet, prevTapCount, etc.) and clear stale intervals
        if (typeof (window as any).__restartGameLoop === 'function') {
          (window as any).__restartGameLoop();
        }
        showToast('Game Reset');
      }
    });
  }
}
